import classNames from 'classnames';
import { Nothing } from 'components/Nothing/Nothing';
import { NOOP } from 'constants/index';
import { useUniqueId } from 'hooks';
import { InfoCircle } from 'icons';
import PropTypes from 'prop-types';
import type React from 'react';
import { type HTMLAttributes, useEffect, useRef, useState } from 'react';
import { createMarkup } from 'utils';

import './Tooltip.scss';

interface InfoProps extends HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  position?: 'bottom' | 'bottom-start' | 'bottom-end';
}

function Info({
  as: Element = 'div',
  children,
  className,
  position = 'bottom',
  ...rest
}: InfoProps) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  return (
    <Element ref={ref} className="tooltip-wrapper" {...rest}>
      <button
        className="tooltip-info"
        onBlur={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => {
          setIsVisible(isVisible => !isVisible);
        }}
      >
        <div
          className="tooltip-info__icon"
          data-testid="tooltip-info__icon"
          aria-describedby="tooltip-info__content"
        >
          <InfoCircle aria-hidden />
        </div>
        <div
          id="tooltip-info__content"
          className={classNames(
            'tooltip-info__content',
            `tooltip-info__content--${position}`,
            isVisible
              ? 'tooltip-info__content--visible'
              : 'tooltip-info__content--hidden',
            className,
          )}
          role="tooltip"
        >
          {isVisible ? children : null}
        </div>
      </button>
    </Element>
  );
}

type TooltipProps = {
  className?: string;
  children?: React.ReactNode;
  defaultMessage?: string;
  successMessage?: string;
  onClick?: (evt: React.MouseEvent<HTMLButtonElement>) => void;
};

function Tooltip({
  className,
  children,
  defaultMessage,
  successMessage,
  onClick = NOOP,
}: TooltipProps) {
  const [message, setMessage] = useState(defaultMessage);
  const [isOpen, setIsOpen] = useState(false);
  const id = useUniqueId('tooltip');

  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage]);

  function handleOnClick(evt: React.MouseEvent<HTMLDivElement>) {
    onClick(evt);

    if (successMessage) {
      setMessage(successMessage);
      return;
    }

    setIsOpen(isOpen => !isOpen);
  }

  function handleOnMouseEnter() {
    setIsOpen(true);
  }

  function handleOnMouseLeave() {
    setMessage(defaultMessage);
    setIsOpen(false);
  }

  if (!message) {
    return <Nothing />;
  }

  return (
    <div
      aria-label={message}
      aria-describedby={id}
      data-testid="container"
      className={['Tooltip-container', ...(className ? [className] : [])].join(
        ' ',
      )}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      onClick={handleOnClick}
      onFocus={handleOnMouseEnter}
      onBlur={handleOnMouseLeave}
      tabIndex={0}
      role="tooltip"
    >
      {children}
      <span
        className={['Tooltip', ...(isOpen ? [] : ['Tooltip-hidden'])].join(' ')}
        aria-hidden={!isOpen}
        data-testid="tooltip"
        id={id}
        dangerouslySetInnerHTML={createMarkup(message)}
      />
    </div>
  );
}

Tooltip.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  defaultMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onClick: PropTypes.func,
  successMessage: PropTypes.string,
};

Tooltip.Info = Info;

export { Tooltip };
